import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import type {
  AppNotification,
  NotificationExport,
  NotificationsPage,
} from "@/lib/types/notification.types";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

function unwrap<T>(fallback: T) {
  return (response: Envelope<T> | undefined) => response?.data ?? fallback;
}

const EMPTY_PAGE: NotificationsPage = { notifications: [], unreadCount: 0 };

/**
 * Notification service API — the bell panel, the full Notifications page,
 * and the export download log under Settings > Exports.
 *
 * Talks to the real Express middleware at `/api/notification/*`, which
 * resolves the user from the session cookie and forwards to the standalone
 * Notification service — no user id is sent from the browser.
 */
export const notificationApi = createApi({
  reducerPath: "notificationApi",
  baseQuery: axiosBaseQuery("/api/notification"),
  tagTypes: ["Notification", "NotificationExport"],
  refetchOnFocus: false,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    /**
     * A page of notifications, newest first, plus the total unread count.
     * Hidden ones are left out unless `hiddenOnly` asks for only those.
     */
    getNotifications: builder.query<
      NotificationsPage,
      { page?: number; limit?: number; unreadOnly?: boolean; hiddenOnly?: boolean } | void
    >({
      query: (args) => {
        const { page = 1, limit = 20, unreadOnly, hiddenOnly } = args ?? {};
        return {
          url: "/notifications",
          params: {
            page,
            limit,
            ...(unreadOnly ? { unreadOnly: true } : {}),
            ...(hiddenOnly ? { hiddenOnly: true } : {}),
          },
        };
      },
      transformResponse: unwrap<NotificationsPage>(EMPTY_PAGE),
      providesTags: ["Notification"],
    }),

    markNotificationRead: builder.mutation<AppNotification | null, string>({
      query: (id) => ({ url: `/notifications/${id}/read`, method: "PATCH" }),
      transformResponse: unwrap<AppNotification | null>(null),
      invalidatesTags: ["Notification"],
    }),

    /** Hides one from the feed; it also comes back marked read. */
    hideNotification: builder.mutation<AppNotification | null, string>({
      query: (id) => ({ url: `/notifications/${id}/hide`, method: "PATCH" }),
      transformResponse: unwrap<AppNotification | null>(null),
      invalidatesTags: ["Notification"],
    }),

    unhideNotification: builder.mutation<AppNotification | null, string>({
      query: (id) => ({ url: `/notifications/${id}/unhide`, method: "PATCH" }),
      transformResponse: unwrap<AppNotification | null>(null),
      invalidatesTags: ["Notification"],
    }),

    markAllNotificationsRead: builder.mutation<{ modifiedCount: number } | null, void>({
      query: () => ({ url: "/notifications/read-all", method: "PATCH" }),
      transformResponse: unwrap<{ modifiedCount: number } | null>(null),
      invalidatesTags: ["Notification"],
    }),

    hideAllNotifications: builder.mutation<{ modifiedCount: number } | null, void>({
      query: () => ({ url: "/notifications/hide-all", method: "PATCH" }),
      transformResponse: unwrap<{ modifiedCount: number } | null>(null),
      invalidatesTags: ["Notification"],
    }),

    /** The export download log — one row per completed export. */
    getNotificationExports: builder.query<
      NotificationExport[],
      { page?: number; limit?: number } | void
    >({
      query: (args) => {
        const { page = 1, limit = 20 } = args ?? {};
        return { url: "/exports", params: { page, limit } };
      },
      transformResponse: unwrap<NotificationExport[]>([]),
      providesTags: ["NotificationExport"],
      // refetchOnMountOrArgChange belongs on the hook call, not the endpoint
      // definition - see useGetNotificationExportsQuery's call site.
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useHideNotificationMutation,
  useUnhideNotificationMutation,
  useMarkAllNotificationsReadMutation,
  useHideAllNotificationsMutation,
  useGetNotificationExportsQuery,
} = notificationApi;

export default notificationApi;
